/*
  LaplandSanta UI helpers for Horizon.
  - <ls-carousel>: scroll-snap carousel with arrow buttons (reviews).
  - <ls-autoplay-video>: muted loop video that only loads and plays while on screen.
*/
const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class LsCarousel extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {HTMLButtonElement | null} */
  #prev = null;
  /** @type {HTMLButtonElement | null} */
  #next = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  #frame = 0;

  connectedCallback() {
    this.#track = this.querySelector('[data-ls-track]');
    this.#prev = this.querySelector('[data-ls-prev]');
    this.#next = this.querySelector('[data-ls-next]');
    if (!this.#track) return;

    this.#prev?.addEventListener('click', this.#onPrev);
    this.#next?.addEventListener('click', this.#onNext);
    this.#track.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#resizeObserver = new ResizeObserver(this.#onScroll);
    this.#resizeObserver.observe(this.#track);
    this.#updateButtons();
  }

  disconnectedCallback() {
    this.#prev?.removeEventListener('click', this.#onPrev);
    this.#next?.removeEventListener('click', this.#onNext);
    this.#track?.removeEventListener('scroll', this.#onScroll);
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #step() {
    const track = this.#track;
    if (!track) return 0;
    const slide = track.querySelector('[data-ls-slide]');
    if (!slide) return track.clientWidth;
    const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
    const slideWidth = slide.getBoundingClientRect().width + gap;
    const perView = Math.max(1, Math.floor((track.clientWidth + gap) / slideWidth));
    return slideWidth * perView;
  }

  /** @param {number} direction */
  #scroll(direction) {
    this.#track?.scrollBy({
      left: direction * this.#step(),
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  #onPrev = () => this.#scroll(-1);
  #onNext = () => this.#scroll(1);

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#updateButtons());
  };

  #updateButtons() {
    const track = this.#track;
    if (!track) return;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);
    const scrollable = maxScroll > 4;

    this.toggleAttribute('data-scrollable', scrollable);
    if (this.#prev) this.#prev.disabled = !scrollable || position <= 4;
    if (this.#next) this.#next.disabled = !scrollable || position >= maxScroll - 4;
  }
}

if (!customElements.get('ls-carousel')) {
  customElements.define('ls-carousel', LsCarousel);
}

/* ------------------------------------------------------------------ */
/* Autoplay video (lazy)                                               */
/* ------------------------------------------------------------------ */

class LsAutoplayVideo extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    const video = this.querySelector('video');
    if (!video) return;

    if (prefersReducedMotion()) {
      video.controls = true;
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            video.play().catch(() => {
              video.controls = true;
            });
          } else {
            video.pause();
          }
        }
      },
      { threshold: 0.25 }
    );
    this.#observer.observe(video);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }
}

if (!customElements.get('ls-autoplay-video')) {
  customElements.define('ls-autoplay-video', LsAutoplayVideo);
}
